import { 
  ArrowDown, 
  AlertTriangle, 
  Users,
  MessageSquare,
  UserMinus,
  Clock,
  ShieldOff,
  Zap
} from "lucide-react";

const skipLevelProblems = [
  {
    title: "Undermines Manager Authority",
    description: "When the CEO gives tasks directly to a developer, the message is clear: 'Your manager doesn't matter.' The team learns to wait for the 'real' boss.",
    icon: ShieldOff,
    example: "CEO → Developer (bypassing Engineering Manager)"
  },
  {
    title: "Creates Conflicting Priorities",
    description: "The manager planned sprint work. The executive assigned something else directly. Now the employee is caught between two bosses with different expectations.",
    icon: Zap,
    example: "Manager says 'finish feature X' / CEO says 'look at this bug first'"
  },
  {
    title: "Destroys Manager Context",
    description: "Managers need to know what their team is working on. Skip-level assignments leave them blind—unable to plan, prioritize, or support their people.",
    icon: MessageSquare,
    example: "Manager learns about a task after it's half-done"
  },
  {
    title: "Erodes Team Trust",
    description: "If feedback comes directly from executives, employees stop trusting their manager's guidance. 'Why listen to my manager if the CEO will just tell me differently?'",
    icon: UserMinus,
    example: "VP gives code review feedback directly to junior dev"
  },
  {
    title: "Creates Bottlenecks at the Top",
    description: "Once people learn the executive will respond directly, they start going straight to them for everything. The executive becomes the bottleneck.",
    icon: Clock,
    example: "Everyone waits for the founder's opinion on every decision"
  },
  {
    title: "Drives Away Good Managers",
    description: "A-player managers want real authority. If their decisions get overridden and their team gets directed around them, they'll leave for somewhere they're respected.",
    icon: Users,
    example: "45% higher turnover for bypassed managers"
  }
];

export const DepartmentalAutonomyProblems = () => {
  return (
    <section className="py-10 sm:py-12">
      <h2 id="skip-level-damage" className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2">
        <ArrowDown className="h-6 w-6 text-destructive" />
        The Damage of Skipping Levels
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Every time a leader goes directly to someone below their direct reports, they create ripple effects throughout the organization. Here's what happens:
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {skipLevelProblems.map((problem) => (
          <div 
            key={problem.title}
            className="bg-card border border-border rounded-lg p-5 hover:border-destructive/30 transition-colors"
          >
            <div className="p-2 bg-destructive/10 rounded-lg w-fit mb-3">
              <problem.icon className="h-5 w-5 text-destructive" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">{problem.title}</h3>
            <p className="text-sm text-muted-foreground mb-3">{problem.description}</p>
            <p className="text-xs text-muted-foreground italic bg-muted/50 px-2 py-1 rounded">
              {problem.example}
            </p>
          </div>
        ))}
      </div>

      {/* Common Excuses Box */}
      <div className="mt-8 bg-destructive/5 border border-destructive/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          Excuses Leaders Use to Justify Skipping Levels
        </h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground mb-1"><strong>"It's faster if I just tell them directly."</strong></p>
            <p className="text-muted-foreground">→ Short-term speed, long-term chaos. You've now trained everyone to bypass the chain.</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1"><strong>"I'm just being helpful."</strong></p>
            <p className="text-muted-foreground">→ You're signaling that the manager can't handle it. Help by supporting the manager, not replacing them.</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1"><strong>"The manager wasn't available."</strong></p>
            <p className="text-muted-foreground">→ Wait, or send a message to the manager first. The urgency is rarely as critical as it feels.</p>
          </div>
          <div>
            <p className="text-muted-foreground mb-1"><strong>"I have a better relationship with that employee."</strong></p>
            <p className="text-muted-foreground">→ Great. Use it through the proper channel. Relationship ≠ permission to bypass hierarchy.</p>
          </div>
        </div>
      </div>
    </section>
  );
};
